<script setup>
import { computed, nextTick, ref, toRef } from 'vue';
import { useI18n } from 'vue-i18n';

import { useZonedTime } from '../composables/useZonedTime';
import HrDate from '../primitives/HrDate.vue';
import HrIcon from '../primitives/HrIcon.vue';
import HrPopupProxy from '../primitives/HrPopupProxy.vue';
import HrTextField from '../primitives/HrTextField.vue';
import HrIconActionBtn from './HrIconActionBtn.vue';

/**
 * Date field — a text field with a calendar popup that reads and writes 'YYYY-MM-DD' calendar strings.
 *
 * A calendar date, not an instant: the model is a plain 'YYYY-MM-DD' string, never a Date or a
 * Firestore Timestamp. Its sibling HrTimeField speaks Timestamps because a time-of-day is one;
 * a date only becomes an instant once a timezone is chosen, and every choice is wrong for someone.
 * Call sites holding a Timestamp convert at the boundary.
 *
 * @status recommended
 * @group forms-input
 * @useWhen Collecting a single calendar date (effective dates, start dates, date of birth)
 * @avoidWhen The value is a moment in time rather than a date — use HrTimeField alongside it
 * @related HrTimeField
 * @related HrDateRange
 * @a11y The field is read-only, so a date can never be mistyped: it opens the calendar on click,
 *   Enter or Space. Both keys are prevented from reaching the surrounding form, so Enter opens the
 *   calendar rather than submitting. Calendar days are keyboard-focusable: arrow keys move between
 *   days, Enter/Space selects.
 */
defineOptions({ name: 'HrDateField' });

const props = defineProps({
  /**
   * Visual container style derived from M3 text field variants.
   * @values filled, outlined
   */
  variant: {
    type: String,
    default: 'outlined',
    validator: (v) => ['filled', 'outlined'].includes(v),
  },
  /**
   * Selected date as a 'YYYY-MM-DD' calendar string, or null.
   * @example 2026-04-06
   */
  modelValue: {
    type: String,
    default: null,
  },
  /** Floating label text shown above the field. */
  label: {
    type: String,
    default: null,
  },
  /**
   * Supporting text below the field: one sentence the reader needs to fill it in.
   * A longer or occasional explanation goes behind an HrIconHintBtn, never in a tooltip.
   */
  hint: {
    type: String,
    default: null,
  },
  /**
   * Predicate controlling which days are selectable; receives a 'YYYY/MM/DD' string (Quasar's
   * calendar format, not the model's) and returns a boolean.
   */
  options: {
    type: Function,
    default: null,
  },
  /** Shows a control that sets the value back to null. */
  clearable: {
    type: Boolean,
    default: false,
  },
  /** Prevents interaction and dims the field. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Shows the value but prevents opening the calendar. */
  readonly: {
    type: Boolean,
    default: false,
  },
  /** Shows a loading spinner inside the field. */
  loading: {
    type: Boolean,
    default: false,
  },
  /** Switches the field to the error color role. */
  error: {
    type: Boolean,
    default: false,
  },
  /** Error text shown below the field when `error` is true. */
  errorMessage: {
    type: String,
    default: null,
  },
  /** Validation rules applied to the field value. */
  rules: {
    type: Array,
    default: null,
  },
  /** When true, validation runs only after the first blur (not on every keystroke). */
  lazyRules: {
    type: Boolean,
    default: false,
  },
  /** Suppresses the reserved space below the field for hint/error messages. */
  hideBottomSpace: {
    type: Boolean,
    default: false,
  },
  /** Reduces the field height to the M3 dense density level. */
  dense: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props forwarded to the underlying Quasar component.
   * @deprecated Only for backwards compatibility with the legacy theme — ignored when a design system theme is active.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
  /**
   * IANA timezone the times are read and written in (e.g. "America/New_York"). The application
   * supplies this from its own timezone policy (e.g. a user preference); the field itself has no
   * knowledge of where it comes from. When null, the host's system timezone is used.
   */
  timeZone: {
    type: String,
    default: null,
  },
});

const emit = defineEmits({
  /** Emitted on change; payload is a 'YYYY-MM-DD' string, or null when the field is cleared. */
  'update:modelValue': null,
});

const { t } = useI18n();
const { getLocaleDateString, adjustDate } = useZonedTime(toRef(props, 'timeZone'));

const isPickerOpen = ref(false);
const textFieldRef = ref(null);

// HrDate's contract is 'YYYY/MM/DD'; the model's is 'YYYY-MM-DD'. Converting here keeps the
// separator out of every call site rather than smuggling it through an undeclared mask attribute.
const pickerValue = computed(() => {
  return props.modelValue === null ? null : props.modelValue.replaceAll('-', '/');
});

// Built from the parts rather than new Date(modelValue): the string form parses as UTC midnight,
// which renders as the previous day anywhere west of UTC.
//
// Formatted with Intl rather than useTime() because the design system may not import app code, and
// rather than toLocaleDateString() because native Date accessors are restricted. Neither is evasion:
// a calendar date carries no instant, so the timezone-awareness those rules protect has nothing to
// act on here — the local-midnight Date below exists only to be handed to the formatter.
const displayValue = computed(() => {
  if (props.modelValue === null || props.modelValue === '') {
    return '';
  }
  const [year, month, day] = props.modelValue.split('-').map(Number);
  if (!year || !month || !day) {
    return props.modelValue;
  }
  return getLocaleDateString(adjustDate(new Date(), { year, month, day }));
});

const isInteractive = computed(() => !props.disable && !props.readonly);

const canClear = computed(() => {
  return props.clearable && isInteractive.value && props.modelValue !== null && props.modelValue !== '';
});

/**
 * Re-runs the field's rules against the value it now holds.
 *
 * WHY IT HAS TO BE ASKED FOR AT ALL: with `lazy-rules` Quasar validates on blur and never on a value
 * change. That is right for a field the user types into, and wrong for this one — it is `readonly`, so
 * its value only ever arrives from the calendar, and the click that opens the calendar is itself what
 * takes focus off the input. An error painted at that moment therefore outlived the user answering it,
 * until they happened to click somewhere that blurred the field again. Picking a day IS the interaction
 * lazy-rules is waiting for.
 *
 * The wait is for the parent's handler and re-render: this component does not own the value, so the
 * rules would otherwise run against the previous prop.
 */
async function revalidate() {
  await nextTick();

  return textFieldRef.value?.validate() ?? true;
}

function onPick(value) {
  // Clicking the selected day again makes the calendar emit null. On a field with no clear control
  // that is the only way to empty it, and it reads as a misclick rather than an intent to clear —
  // so only a clearable field honors it.
  if (value === null || value === undefined) {
    if (props.clearable) {
      emit('update:modelValue', null);
      revalidate();
    }
    isPickerOpen.value = false;
    return;
  }
  emit('update:modelValue', value.replaceAll('/', '-'));
  isPickerOpen.value = false;
  revalidate();
}

function onFieldClick() {
  if (isInteractive.value) {
    isPickerOpen.value = true;
  }
}

function onClear() {
  emit('update:modelValue', null);
  isPickerOpen.value = false;
  revalidate();
}

defineExpose({
  /**
   * Re-runs this field's rules and resolves to whether they pass.
   *
   * For a rule that reads ANOTHER field: when the value it compares against moves, this field's own
   * value has not changed, so nothing re-checks it. A date range is the standard case — an end date
   * flagged as before the start stays flagged after the start is corrected.
   */
  validate: () => revalidate(),
});
</script>

<template>
  <hr-text-field
    ref="textFieldRef"
    class="hr-date-field"
    :class="{ 'hr-date-field--interactive': isInteractive }"
    :model-value="displayValue"
    :variant="variant"
    :label="label"
    :hint="hint"
    :disable="disable"
    :loading="loading"
    :error="error"
    :error-message="errorMessage"
    :rules="rules"
    :lazy-rules="lazyRules"
    :hide-bottom-space="hideBottomSpace"
    :dense="dense"
    :legacy-props="legacyProps"
    readonly
    @click="onFieldClick"
    @keydown.enter.prevent="onFieldClick"
    @keydown.space.prevent="onFieldClick"
  >
    <template #prepend>
      <hr-icon name="event" />
      <!-- Mounted inside the field rather than as a sibling so the popup anchors to it; `readonly`
           above is unconditional, so `isInteractive` is what actually gates opening.

           The anchor is the prepend cell, which spans the full control height, so `bottom start`
           puts the calendar under the field's leading edge. Quasar's `cover` would centre it on
           the icon instead, and its off-screen correction then pins a calendar that wide to
           `left: 0` — outside any dialog the field sits in. -->
      <hr-popup-proxy
        v-if="isInteractive"
        v-model="isPickerOpen"
        anchor="bottom start"
        self="top start"
        transition-show="scale"
        transition-hide="scale"
      >
        <hr-date
          :model-value="pickerValue"
          :options="options"
          data-e2e="date-picker"
          @update:model-value="onPick"
        />
      </hr-popup-proxy>
    </template>

    <!-- Clear control and the append slot share one template: two <template #append> elements would
         target the same slot, and Vue keeps only the last. -->
    <template
      v-if="canClear || $slots.append"
      #append
    >
      <!-- Quasar's own `clearable` needs an editable field, which this never is, so clearing is an
           explicit action. It stops propagation, or the field's click handler reopens the calendar
           the moment the value is cleared. -->
      <hr-icon-action-btn
        v-if="canClear"
        icon="close"
        size="sm"
        :aria-label="t('common.buttonClear')"
        data-e2e="btn-clear-date"
        @click.stop="onClear"
      />
      <!-- @slot append Content after the field value (e.g. a source or status indicator). -->
      <slot name="append" />
    </template>

    <template
      v-if="$slots.hint"
      #hint="slotProps"
    >
      <!-- @slot hint Custom hint content below the field. -->
      <slot
        name="hint"
        v-bind="slotProps ?? {}"
      />
    </template>
  </hr-text-field>
</template>

<style lang="scss" scoped>
/*
 * The field is Quasar-readonly whether or not the caller asked for it, so a date can only come
 * from the calendar. That pulls in two conventions meant for a genuinely non-editable field: a
 * dashed outline, and an I-beam over a populated value (`.q-field--readonly.q-field--float`).
 * Both are dropped only while the field is interactive, so the dash goes back to marking a field
 * the caller made `readonly` — an integration-managed date, say — instead of marking every date
 * field. The selectors carry `.q-field--readonly` so they outrank Quasar's rather than tying with
 * it on specificity, and they hold in the legacy theme, where HrTextField's own border rules are
 * not applied at all.
 *
 * A pointer over a field that opens a popup instead of taking typed input is Quasar's own
 * convention for `.q-select--without-input`. `disable` keeps not-allowed from `.q-field__inner`.
 */
.hr-date-field--interactive.q-field--readonly :deep(.q-field__control::before) {
  border-style: solid;
}

.hr-date-field--interactive.q-field--readonly :deep(.q-field__control),
.hr-date-field--interactive.q-field--readonly :deep(.q-field__native) {
  cursor: pointer;
}
</style>
